import { useState, useCallback } from 'react';
import axios from 'axios';

const Settings = require('settings.json');
const API_BASE = Settings[process.env.NODE_ENV]?.server_url || '';

function getAuthToken() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.token || '';
  } catch {
    return '';
  }
}

function parseSseEvents(buffer) {
  const events = [];
  const lines = buffer.split('\n');
  let currentEvent = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      if (currentEvent) {
        events.push(currentEvent);
        currentEvent = null;
      }
      continue;
    }

    if (trimmed.startsWith('event:')) {
      currentEvent = { event: trimmed.slice(6).trim(), data: '' };
    } else if (trimmed.startsWith('data:') && currentEvent) {
      currentEvent.data = trimmed.slice(5).trim();
    }
  }

  return events;
}

export function useCopilot() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [error, setError] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [view, setView] = useState('chat');

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get('/api/copilot/conversations');
      setConversations((res.data.conversations || []).slice(0, 10));
    } catch (err) {
      console.error('Failed to load conversations', err);
    }
  }, []);

  const loadConversation = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/api/copilot/conversations/${id}`);
      const conversation = res.data.conversation;
      if (conversation) {
        setConversationId(conversation.id);
        setMessages(conversation.messages || []);
        setView('chat');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load conversation.');
    } finally {
      setLoading(false);
    }
  }, []);

  const startNewChat = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setError(null);
    setView('chat');
  }, []);

  const sendMessage = useCallback(
    async (text) => {
      if (!text.trim()) return;

      setLoading(true);
      setStreaming(true);
      setError(null);
      setMessages((prev) => [...prev, { role: 'user', content: text }]);

      try {
        const token = getAuthToken();
        const res = await fetch(`${API_BASE}/api/copilot/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            message: text,
            conversation_id: conversationId,
            stream: true,
          }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || 'Request failed');
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let streamedContent = '';
        let doneData = null;

        // Add an empty assistant placeholder that will be filled incrementally.
        setMessages((prev) => [...prev, { role: 'assistant', content: '', tool_calls: [] }]);

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const events = parseSseEvents(buffer);
          // Keep any trailing incomplete event in the buffer.
          const lastDoubleNewline = buffer.lastIndexOf('\n\n');
          buffer = lastDoubleNewline >= 0 ? buffer.slice(lastDoubleNewline + 2) : buffer;

          for (const event of events) {
            if (!event.data) continue;

            let data;
            try {
              data = JSON.parse(event.data);
            } catch {
              continue;
            }

            if (event.event === 'chunk') {
              streamedContent += data.content || '';
              const contentToShow = streamedContent;
              setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (last?.role === 'assistant') {
                  return [...prev.slice(0, -1), { ...last, content: contentToShow }];
                }
                return prev;
              });
            } else if (event.event === 'done') {
              doneData = data;
            } else if (event.event === 'error') {
              throw new Error(data.message || 'Streaming failed');
            }
          }
        }

        if (doneData) {
          setConversationId(doneData.conversation_id);
          if (doneData.message) {
            setMessages((prev) => [...prev.slice(0, -1), doneData.message]);
          }
          fetchConversations();
        }
      } catch (err) {
        setError(err.message || 'Something went wrong. Please try again.');
        // Remove the empty assistant placeholder on error.
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === 'assistant' && !last.content) {
            return prev.slice(0, -1);
          }
          return prev;
        });
      } finally {
        setLoading(false);
        setStreaming(false);
      }
    },
    [conversationId, fetchConversations]
  );

  const reset = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setError(null);
    setConversations([]);
    setView('chat');
  }, []);

  return {
    messages,
    loading,
    streaming,
    conversationId,
    error,
    conversations,
    view,
    setView,
    sendMessage,
    reset,
    fetchConversations,
    loadConversation,
    startNewChat,
  };
}
